import React from 'react';
import { useTranslation } from 'react-i18next';

export const useIndustryData = () => {
    const { t } = useTranslation();

    const renderInfoReason = (translationPath) => {
        const items = t(translationPath, { returnObjects: true });
        if (!Array.isArray(items)) return null;
        return (
            <ul style={{ paddingLeft: '14px', margin: 0, listStyleType: 'disc', textAlign: 'left' }}>
                {items.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: idx === items.length - 1 ? '0' : '4px' }} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
            </ul>
        );
    };

    return [
        {
            id: 'telemedicine',
            title: t('industryApplicationsHealthcare.data.telemedicine.title'),
            subtitle: t('industryApplicationsHealthcare.data.telemedicine.subtitle'),
            problem: t('industryApplicationsHealthcare.data.telemedicine.problem'),
            impact: t('industryApplicationsHealthcare.data.telemedicine.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.title'), 
                    text: t('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.text'),
                    renderType: 'heatmap',
                    mediaImage: require('./assets/application/telemedicine_1.webp'),
                    regions: [
                        // Top left background area
                        { id: 'background', box: [0.0, 0.0, 0.2, 0.2], reason: t('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.regions.background'), severity: 7, position: 'right' },
                        // Bottom of the blue bowl
                        { id: 'rim', box: [0.3, 0.85, 0.6, 0.98], reason: t('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.regions.rim'), severity: 7, position: 'top' },
                        // The chopsticks on the right
                        { id: 'chopsticks', box: [0.75, 0.2, 0.95, 0.8], reason: t('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.regions.chopsticks'), severity: 8, position: 'left' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.935, 0.9, 0.00, 0.00], position: 'top',
                        title: t('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.infoTitle'),
                        reason: renderInfoReason('industryApplicationsHealthcare.data.telemedicine.solutions.heatmap.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsHealthcare.data.telemedicine.solutions.pipeline.title'), 
                    text: t('industryApplicationsHealthcare.data.telemedicine.solutions.pipeline.text'),
                    renderType: 'telehealth-pipeline' 
                },
            ]
        },
        {
            id: 'data_sanitization',
            title: t('industryApplicationsHealthcare.data.data_sanitization.title'),
            subtitle: t('industryApplicationsHealthcare.data.data_sanitization.subtitle'),
            problem: t('industryApplicationsHealthcare.data.data_sanitization.problem'),
            impact: t('industryApplicationsHealthcare.data.data_sanitization.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.title'), 
                    text: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.text'),
                    renderType: 'text-highlight',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=0120fd5729f6779bb2584b87ad325c1e70fbc740527b77ead6121a38dd42d9ae&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    textContent: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.textContent'),
                    regions: [
                        { 
                            quote: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[0]?.quote, 
                            reason: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[0]?.reason, 
                            severity: 8
                        },
                        { 
                            quote: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[1]?.quote, 
                            reason: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[1]?.reason, 
                            severity: 8 
                        },
                        { 
                            quote: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[2]?.quote, 
                            reason: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[2]?.reason, 
                            severity: 9 
                        },
                        { 
                            quote: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[3]?.quote, 
                            reason: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[3]?.reason, 
                            severity: 9 
                        },
                        { 
                            quote: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[4]?.quote, 
                            reason: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[4]?.reason, 
                            severity: 9 
                        },
                        { 
                            quote: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[5]?.quote, 
                            reason: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.regions', { returnObjects: true })[5]?.reason, 
                            severity: 10 
                        }
                    ],
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.infoTitle'),
                        reason: renderInfoReason('industryApplicationsHealthcare.data.data_sanitization.solutions.textScan.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsHealthcare.data.data_sanitization.solutions.pipeline.title'), 
                    text: t('industryApplicationsHealthcare.data.data_sanitization.solutions.pipeline.text'),
                    renderType: 'phi-pipeline' 
                }
            ]
        },
        {
            id: 'compliance_ledger',
            title: t('industryApplicationsHealthcare.data.compliance_ledger.title'),
            subtitle: t('industryApplicationsHealthcare.data.compliance_ledger.subtitle'),
            problem: t('industryApplicationsHealthcare.data.compliance_ledger.problem'),
            impact: t('industryApplicationsHealthcare.data.compliance_ledger.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsHealthcare.data.compliance_ledger.solutions.audioForensics.title'), 
                    text: t('industryApplicationsHealthcare.data.compliance_ledger.solutions.audioForensics.text'),
                    renderType: 'audio-player',
                    mediaImage: require('./assets/application/compliance_ledger_0.webp'), 
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/audio_compliance_ledger.mp3',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=e51980c7eb9c50d2cf906439b2f68da3cd65fb9bac14da2917bae050ece047d8&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsHealthcare.data.compliance_ledger.solutions.audioForensics.infoTitle'),
                        reason: renderInfoReason('industryApplicationsHealthcare.data.compliance_ledger.solutions.audioForensics.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsHealthcare.data.compliance_ledger.solutions.ledger.title'), 
                    text: t('industryApplicationsHealthcare.data.compliance_ledger.solutions.ledger.text'),
                    renderType: 'chain-of-custody' 
                }
            ]
        },
        {
            id: 'insurance',
            title: t('industryApplicationsHealthcare.data.insurance.title'),
            subtitle: t('industryApplicationsHealthcare.data.insurance.subtitle'),
            problem: t('industryApplicationsHealthcare.data.insurance.problem'),
            impact: t('industryApplicationsHealthcare.data.insurance.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsHealthcare.data.insurance.solutions.heatmap.title'), 
                    text: t('industryApplicationsHealthcare.data.insurance.solutions.heatmap.text'),
                    renderType: 'heatmap',
                    // Make sure the extension matches what you saved the image as (.webp, .jpg, or .png)
                    mediaImage: require('./assets/application/insurance_0.webp'), 
                    regions: [
                        { 
                            id: 'artifact', 
                            box: [0.825, 0.37, 0.895, 0.395], // Directly from DB
                            reason: t('industryApplicationsHealthcare.data.insurance.solutions.heatmap.regions.artifact'), 
                            severity: 8, 
                            position: 'left' 
                        },
                        { 
                            id: 'total', 
                            box: [0.775, 0.685, 0.885, 0.725], // Directly from DB
                            reason: t('industryApplicationsHealthcare.data.insurance.solutions.heatmap.regions.total'), 
                            severity: 10, 
                            position: 'top' 
                        }
                    ],
                    infoPoint: {
                        id: 'info', 
                        box: [0.905, 0.92, 0.00, 0.00], 
                        position: 'top',
                        title: t('industryApplicationsHealthcare.data.insurance.solutions.heatmap.infoTitle'),
                        reason: renderInfoReason('industryApplicationsHealthcare.data.insurance.solutions.heatmap.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsHealthcare.data.insurance.solutions.pipeline.title'), 
                    text: t('industryApplicationsHealthcare.data.insurance.solutions.pipeline.text'),
                    renderType: 'insurance-pipeline' 
                }
            ]
        },
        {
            id: 'data_leak', // Updated ID
            title: t('industryApplicationsHealthcare.data.data_leak.title'),
            subtitle: t('industryApplicationsHealthcare.data.data_leak.subtitle'),
            problem: t('industryApplicationsHealthcare.data.data_leak.problem'),
            impact: t('industryApplicationsHealthcare.data.data_leak.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.title'), 
                    text: t('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.text'),
                    renderType: 'heatmap',
                    mediaImage: require('./assets/application/data_leak_0.webp'), 
                    regions: [
                        { 
                            id: 'name', 
                            box: [0.493, 0.34, 0.57, 0.37],
                            reason: t('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.regions.name'), 
                            severity: 8, 
                            position: 'right' 
                        },
                        { 
                            id: 'phone', 
                            box: [0.493, 0.37, 0.6, 0.4], 
                            reason: t('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.regions.phone'), 
                            severity: 9, 
                            position: 'right' 
                        },
                        { 
                            id: 'doctor', 
                            box: [0.493, 0.492, 0.63, 0.535], 
                            reason: t('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.regions.doctor'), 
                            severity: 6, 
                            position: 'right' 
                        }
                    ],
                    infoPoint: {
                        id: 'info', 
                        box: [0.9, 0.9, 0.00, 0.00], 
                        position: 'top',
                        title: t('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.infoTitle'),
                        reason: renderInfoReason('industryApplicationsHealthcare.data.data_leak.solutions.heatmap.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsHealthcare.data.data_leak.solutions.pipeline.title'), 
                    text: t('industryApplicationsHealthcare.data.data_leak.solutions.pipeline.text'),
                    renderType: 'phi-pipeline' 
                }
            ]
        },
        // {
        //     id: 'identity_kyc',
        //     title: t('industryApplicationsHealthcare.data.identity_kyc.title'),
        //     subtitle: t('industryApplicationsHealthcare.data.identity_kyc.subtitle'),
        //     problem: t('industryApplicationsHealthcare.data.identity_kyc.problem'),
        //     impact: t('industryApplicationsHealthcare.data.identity_kyc.impact'),
        //     solutionPoints: [
        //         { 
        //             title: t('industryApplicationsHealthcare.data.identity_kyc.solutions.pipeline.title'), 
        //             text: t('industryApplicationsHealthcare.data.identity_kyc.solutions.pipeline.text'),
        //             renderType: 'kyc-pipeline' 
        //         }
        //     ]
        // },
        // {
        //     id: 'medical_devices',
        //     title: t('industryApplicationsHealthcare.data.medical_devices.title'),
        //     subtitle: t('industryApplicationsHealthcare.data.medical_devices.subtitle'),
        //     problem: t('industryApplicationsHealthcare.data.medical_devices.problem'),
        //     impact: t('industryApplicationsHealthcare.data.medical_devices.impact'),
        //     solutionPoints: [
        //         { 
        //             title: t('industryApplicationsHealthcare.data.medical_devices.solutions.shield.title'), 
        //             text: t('industryApplicationsHealthcare.data.medical_devices.solutions.shield.text'),
        //             renderType: 'shield',
        //             mediaType: 'image',
        //             mediaUrl: require('../IndustryApplicationsAmazon/assets/application/sponsored_brands_0.webp'),
        //             verifyUrl: 'https://www.vetra-ai.com/verify?id=demo',
        //             shieldIcon: require('../IndustryApplicationsAmazon/assets/application/vetraai_logo_advertising_1.webp'),
        //             shieldPosition: 'top-left'
        //         }
        //     ]
        // },
        {
            id: 'crisis_management',
            title: t('industryApplicationsHealthcare.data.crisis_management.title'),
            subtitle: t('industryApplicationsHealthcare.data.crisis_management.subtitle'),
            problem: t('industryApplicationsHealthcare.data.crisis_management.problem'),
            impact: t('industryApplicationsHealthcare.data.crisis_management.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplicationsHealthcare.data.crisis_management.solutions.videoForensics.title'), 
                    text: t('industryApplicationsHealthcare.data.crisis_management.solutions.videoForensics.text'),
                    renderType: 'video-player', // <--- Use the new Video Player
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/video_crisis_management.mp4',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=924f2dbc946d8a49dec14929a436c84a2d80f0cffdfbcf2aaf949065bc4cbf5e&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&context=fake_news_detection&regions=true&explanation=true',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    infoPoint: {
                        id: 'info', position: 'left', // Ensure it doesn't block the play button
                        title: t('industryApplicationsHealthcare.data.crisis_management.solutions.videoForensics.infoTitle'),
                        reason: renderInfoReason('industryApplicationsHealthcare.data.crisis_management.solutions.videoForensics.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsHealthcare.data.crisis_management.solutions.pipeline.title'), 
                    text: t('industryApplicationsHealthcare.data.crisis_management.solutions.pipeline.text'),
                    renderType: 'crisis-pipeline' // <--- Use the new Pipeline
                }
            ]
        }
    ];
};